import React, { useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { ShippingAddressFormValues, AddressSearchResult } from './types';
import { useShippingAddressForm } from './hooks/useShippingAddressForm';
import { TextInput } from 'src/components/text-input/TextInput';
import { AddressSearchField } from './AddressSearchField';
import colors from 'src/styles/equalution/colors';

type Props = {
  onValidityChange?: (isValid: boolean, dirty: boolean) => void;
  formRef?: React.MutableRefObject<ReturnType<
    typeof useShippingAddressForm
  > | null>;
  hideTitle?: boolean;
};

export const ShippingAddressForm: React.FC<Props> = ({
  onValidityChange,
  formRef,
  hideTitle = false,
}) => {
  const formik = useShippingAddressForm(onValidityChange);
  const [showAddressFields, setShowAddressFields] = useState(false);
  const [hideAddressSearch, setHideAddressSearch] = useState(false);

  if (formRef) {
    formRef.current = formik;
  }

  const getError = (name: keyof ShippingAddressFormValues) =>
    formik.touched[name] && formik.errors[name]
      ? (formik.errors[name] as string)
      : undefined;

  const handleAddressSelect = (address: Partial<AddressSearchResult>) => {
    formik.setFieldValue('streetAddress', address?.streetAddress || '');
    formik.setFieldValue('suburbCity', address?.suburbCity || '');
    formik.setFieldValue('stateProvince', address?.state || '');
    formik.setFieldValue('postalCode', address?.postalCode || '');
    formik.setFieldValue('country', address?.country || 'Australia');

    // Mark address fields as touched and trigger validation
    const addressFields = [
      'streetAddress',
      'suburbCity',
      'stateProvince',
      'postalCode',
      'country',
    ] as const;
    addressFields.forEach(field => formik.setFieldTouched(field, true));

    setShowAddressFields(true);

    // Trigger Deferred validation after a short delay to ensure values are set
    setTimeout(() => {
      formik.validateForm();
    }, 0);
  };

  const handleManualEntry = () => {
    setShowAddressFields(true);
    setHideAddressSearch(true);
    formik.setFieldValue('addressSearch', '');
  };

  return (
    <Box sx={{ width: '100%' }} display="flex" flexDirection="column" gap={3}>
      {!hideTitle && (
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            color: colors.OFF_BLACK,
            textAlign: 'left',
          }}>
          Shipping Address
        </Typography>
      )}
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 400,
          color: colors.BLUEBERRY_HALF,
          textAlign: 'left',
          marginTop: '-4px',
        }}>
        Delivery within 3-5 business days
      </Typography>
      <Grid container spacing={{ xs: 1.5, md: 2 }}>
        <Grid item xs={12} md={4} sx={{ overflow: 'hidden' }}>
          <TextInput
            label="First Name"
            id="firstName"
            placeholder="Stevie"
            {...formik.getFieldProps('firstName')}
            errorText={getError('firstName')}
          />
        </Grid>
        <Grid item xs={12} md={4} sx={{ overflow: 'hidden' }}>
          <TextInput
            label="Last Name"
            id="lastName"
            placeholder="Wild"
            {...formik.getFieldProps('lastName')}
            errorText={getError('lastName')}
          />
        </Grid>
        <Grid item xs={12} md={4} sx={{ overflow: 'hidden' }}>
          <TextInput
            label="Phone Number"
            id="mobilePhone"
            {...formik.getFieldProps('mobilePhone')}
            errorText={getError('mobilePhone')}
          />
        </Grid>

        {!hideAddressSearch && (
          <Grid item xs={12}>
            <AddressSearchField
              label="Address"
              placeholder="Type to search address"
              {...formik.getFieldProps('addressSearch')}
              onAddressSelect={handleAddressSelect}
              onManualEntry={handleManualEntry}
              errorText={getError('addressSearch')}
            />
          </Grid>
        )}

        {showAddressFields && (
          <>
            <Grid item xs={12} md={6} sx={{ overflow: 'hidden' }}>
              <TextInput
                label="Street Address"
                id="streetAddress"
                {...formik.getFieldProps('streetAddress')}
                errorText={getError('streetAddress')}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ overflow: 'hidden' }}>
              <TextInput
                label="City"
                id="suburbCity"
                {...formik.getFieldProps('suburbCity')}
                errorText={getError('suburbCity')}
              />
            </Grid>

            <Grid item xs={12} md={4} sx={{ overflow: 'hidden' }}>
              <TextInput
                label="State/Territory"
                id="stateProvince"
                {...formik.getFieldProps('stateProvince')}
                errorText={getError('stateProvince')}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ overflow: 'hidden' }}>
              <TextInput
                label="Postcode"
                id="postalCode"
                {...formik.getFieldProps('postalCode')}
                errorText={getError('postalCode')}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ overflow: 'hidden' }}>
              <TextInput
                label="Country"
                id="country"
                {...formik.getFieldProps('country')}
                disabled
                errorText={getError('country')}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};
